import { Bars4Icon } from '@heroicons/react/24/outline';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { MIN_MAIN_CONTENT_WIDTH } from '@/shared/lib/constants';
import { Button } from '../../button/Button';
import { GlobalNavigation } from '../global-navigation/GlobalNavigation';

type BaseLayoutProps = {
  /** GlobalNavigation の追加クラス */
  headerClassName?: string;
  /** GlobalNavigation の左セクション（トグルボタンの右に追加表示） */
  headerLeftSection?: ReactNode;
  /** GlobalNavigation の右セクション */
  headerRightSection?: ReactNode;
  /** トグルボタンクリック時のハンドラー（未指定時はトグルボタン非表示） */
  onToggleSidebar?: () => void;
  /** 左サイドバー（SidePanel インスタンスをそのまま渡す） */
  sidebar?: ReactNode;
  /** 右パネル（SidePanel インスタンスをそのまま渡す） */
  rightPanel?: ReactNode;
  /** メインコンテンツ */
  children: ReactNode;
  /** メインコンテンツ領域のref（スクロール同期用） */
  mainRef?: RefObject<HTMLDivElement | null>;
  /** メインコンテンツの最小幅（デフォルト: MIN_MAIN_CONTENT_WIDTH） */
  mainMinWidth?: number;
  /** レイアウト外に表示する追加要素（SidePanelLeaveAlert, DebugMenu等） */
  extra?: ReactNode;
};

/**
 * アプリケーション共通のベースレイアウト
 *
 * GlobalNavigation（Bars4Icon トグルボタン付き）+ 左サイドバー + メインコンテンツ + 右パネルの構造を提供する。
 * MainRouteLayout と FocusViewPageLayout の共通基盤。
 */
export const BaseLayout = ({
  headerClassName,
  headerLeftSection,
  headerRightSection,
  onToggleSidebar,
  sidebar,
  rightPanel,
  children,
  mainRef,
  mainMinWidth = MIN_MAIN_CONTENT_WIDTH,
  extra,
}: BaseLayoutProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <GlobalNavigation
        className={headerClassName}
        leftSection={
          <div className="flex items-center gap-4">
            {onToggleSidebar && (
              <Button
                variant="outline"
                size="icon"
                onClick={onToggleSidebar}
                aria-label={t('common.actions.toggle-sidebar')}
              >
                <Bars4Icon />
              </Button>
            )}
            {headerLeftSection}
          </div>
        }
        rightSection={headerRightSection}
      />
      <div className="relative flex flex-1 overflow-hidden">
        {sidebar}
        <main ref={mainRef} className="relative flex-1 overflow-auto bg-stone-50">
          <div style={{ minWidth: `${mainMinWidth}px` }}>{children}</div>
        </main>
        {rightPanel}
      </div>
      {extra}
    </div>
  );
};
